import { supabase } from '@/lib/supabase'
import { getLatestStripeSubscriptionSyncCandidate, syncSubscriptionStatus } from '@/modules/plans/services/memberPlanService'

export type InvoiceItem = {
  id: string
  number: string | null
  status: string | null
  amount_due_cents: number
  amount_paid_cents: number
  currency: string
  created_at: string | null
  period_start: string | null
  period_end: string | null
  hosted_invoice_url: string | null
  invoice_pdf: string | null
  subscription_id: string | null
  line_description: string | null
  account_id?: string | null
}

const BILLING_PORTAL_SYNC_PENDING_KEY = 'billing:portal:sync_pending'

function readPortalSyncPendingTimestamp(): number | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(BILLING_PORTAL_SYNC_PENDING_KEY)
    const ts = Number(raw)
    return Number.isFinite(ts) ? ts : null
  } catch {
    return null
  }
}

export function markBillingPortalSyncPending(nowMs: number = Date.now()): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(BILLING_PORTAL_SYNC_PENDING_KEY, String(nowMs))
  } catch {
    // ignore
  }
}

export function clearBillingPortalSyncPending(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(BILLING_PORTAL_SYNC_PENDING_KEY)
  } catch {
    // ignore
  }
}

export function hasBillingPortalSyncPending(
  nowMs: number = Date.now(),
  maxAgeMs: number = 60 * 60 * 1000,
): boolean {
  const ts = readPortalSyncPendingTimestamp()
  if (ts === null) return false
  return (nowMs - ts) <= maxAgeMs
}

export async function syncBillingPortalReturn(userId?: string | null): Promise<boolean> {
  const uid = String(userId || '').trim()
  if (!uid) {
    clearBillingPortalSyncPending()
    return false
  }
  try {
    const candidateId = await getLatestStripeSubscriptionSyncCandidate(uid)
    if (!candidateId) {
      clearBillingPortalSyncPending()
      return false
    }
    await syncSubscriptionStatus(candidateId)
    clearBillingPortalSyncPending()
    return true
  } catch {
    return false
  }
}

export async function listMyInvoices(): Promise<InvoiceItem[]> {
  const { data, error } = await supabase.functions.invoke('member-list-invoices', { body: {} as any })
  if (error) throw error
  const payload = (Array.isArray(data) ? (data as any)[0] : (data as any)) || {}
  return (payload.items || []) as InvoiceItem[]
}

export type SubSummary = {
  id: string
  account_id?: string | null
  plan_name: string | null
  amount_cents: number | null
  currency: string | null
  interval: 'month'|'year'|null
  status: string | null
  created_at: string | null
  current_period_end: string | null
  stripe_subscription_id?: string | null
}

export async function listMySubscriptionsSummary(): Promise<SubSummary[]> {
  const { data, error } = await supabase.functions.invoke('member-list-subscriptions', { body: {} as any })
  if (error) throw error
  const payload = (Array.isArray(data) ? (data as any)[0] : (data as any)) || {}
  const items = (payload.items || []) as any[]
  return items.map((it) => ({
    id: it.id,
    account_id: it.account_id || null,
    plan_name: it.plan_name ?? null,
    amount_cents: typeof it.amount_cents === 'number' ? it.amount_cents : null,
    currency: it.currency || 'BRL',
    interval: it.interval || null,
    status: it.status || null,
    created_at: it.created_at || null,
    current_period_end: it.current_period_end || null,
    stripe_subscription_id: it.stripe_subscription_id || null,
  }))
}

export async function openStripeBillingPortal(return_url?: string): Promise<void> {
  markBillingPortalSyncPending()
  // Tenta via supabase.functions.invoke; se falhar, faz fetch direto para capturar o body de erro
  const envPayload: any = (() => {
    try {
      const host = (typeof window !== 'undefined' ? (window.location?.hostname || '') : '').toLowerCase()
      const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '::1' || host.endsWith('.local')
      return { client_env: (isLocal ? 'local' : 'site'), client_host: host }
    } catch { return {} }
  })()
  const { data, error } = await supabase.functions.invoke('create-billing-portal-session', { body: { return_url, ...envPayload } as any })
  let payload: any = (Array.isArray(data) ? (data as any)[0] : (data as any)) || {}
  if (error) {
    try {
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || ''
      const baseRaw: string = ((supabase as any).functions?.url as string | undefined) || (import.meta as any)?.env?.VITE_SUPABASE_URL || ''
      const base = (baseRaw || '').replace(/\/+$/, '')
      const fnBase = base.endsWith('/functions/v1') ? base : `${base}/functions/v1`
      const resp = await fetch(`${fnBase}/create-billing-portal-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ return_url, ...envPayload })
      })
      payload = await resp.json().catch(()=>({}))
      if (resp.ok && payload?.url) { window.open(payload.url as string, '_blank', 'noopener,noreferrer'); return }
      const detail = payload?.error || `HTTP ${resp.status}`
      clearBillingPortalSyncPending()
      throw new Error(payload?.hint ? `${detail} — ${payload.hint}` : detail)
    } catch (e: any) {
      clearBillingPortalSyncPending()
      const detail = (payload && payload.error) ? String(payload.error) : (e?.message || (error as any)?.message)
      throw new Error(detail || 'Falha ao abrir o Portal do Stripe')
    }
  }
  const urlOk = payload?.url as string | undefined
  if (!urlOk) {
    clearBillingPortalSyncPending()
    throw new Error('Falha ao criar sessão do Portal Stripe')
  }
  window.open(urlOk, '_blank', 'noopener,noreferrer')
}
